import { supabase } from '../lib/supabase';

class PunchHistoryService {
  /**
   * Fetch punch history for a specific employee
   */
  async getPunchHistory(employeeId, options = {}) {
    try {
      const {
        limit = 30, // Default to last 30 entries
        startDate = null,
        endDate = null,
        includeIncomplete = true
      } = options;

      let query = supabase
        .from('time_entries')
        .select(`
          id,
          employee_id,
          clock_in_time,
          clock_out_time,
          entry_type,
          total_hours,
          notes,
          break_duration,
          created_at,
          updated_at
        `)
        .eq('employee_id', employeeId)
        .order('clock_in_time', { ascending: false });

      // Apply date filters if provided
      if (startDate) {
        query = query.gte('clock_in_time', startDate);
      }
      if (endDate) {
        query = query.lte('clock_in_time', endDate);
      }

      // Filter out incomplete entries if requested
      if (!includeIncomplete) {
        query = query.not('clock_out_time', 'is', null);
      }

      // Apply limit
      query = query.limit(limit);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching punch history:', error);
        throw error;
      }

      // Transform data to match UI expectations
      return this.transformPunchHistoryData(data || []);
    } catch (error) {
      console.error('Error in getPunchHistory:', error);
      throw error;
    }
  }

  /**
   * Get punch history summary for an employee
   */
  async getPunchHistorySummary(employeeId, options = {}) {
    try {
      const {
        startDate = null,
        endDate = null
      } = options;

      let query = supabase
        .from('time_entries')
        .select('clock_in_time, clock_out_time, total_hours, entry_type')
        .eq('employee_id', employeeId)
        .not('clock_out_time', 'is', null); // Only completed entries

      // Apply date filters if provided
      if (startDate) {
        query = query.gte('clock_in_time', startDate);
      }
      if (endDate) {
        query = query.lte('clock_in_time', endDate);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching punch history summary:', error);
        throw error;
      }

      return this.calculateSummary(data || []);
    } catch (error) {
      console.error('Error in getPunchHistorySummary:', error);
      throw error;
    }
  }

  /**
   * Get recent punch history (last 7 days)
   */
  async getRecentPunchHistory(employeeId) {
    try {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      return await this.getPunchHistory(employeeId, {
        startDate: sevenDaysAgo.toISOString(),
        limit: 20
      });
    } catch (error) {
      console.error('Error in getRecentPunchHistory:', error);
      throw error;
    }
  }

  /**
   * Transform raw database data to UI-friendly format
   */
  transformPunchHistoryData(data) {
    return data.map(entry => {
      const clockInTime = entry.clock_in_time ? new Date(entry.clock_in_time) : null;
      const clockOutTime = entry.clock_out_time ? new Date(entry.clock_out_time) : null;
      
      // Calculate hours worked
      let hours = 0;
      if (clockInTime && clockOutTime) {
        const diffMs = clockOutTime.getTime() - clockInTime.getTime();
        hours = Math.round((diffMs / (1000 * 60 * 60)) * 100) / 100; // Round to 2 decimal places
      }

      // Format times for display
      const formatTime = (date) => {
        if (!date) return '-';
        return date.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      };

      // Format date for display
      const formatDate = (date) => {
        if (!date) return '-';
        return date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      };

      return {
        id: entry.id,
        date: formatDate(clockInTime),
        clockIn: formatTime(clockInTime),
        clockOut: formatTime(clockOutTime),
        hours: hours > 0 ? hours.toString() : (entry.total_hours ? entry.total_hours.toString() : '0'),
        entryType: entry.entry_type || 'regular',
        status: clockOutTime ? 'completed' : 'incomplete',
        notes: entry.notes || '',
        breakDuration: entry.break_duration || 0,
        rawData: {
          clockInTime: entry.clock_in_time,
          clockOutTime: entry.clock_out_time,
          totalHours: entry.total_hours
        }
      };
    });
  }

  /**
   * Calculate summary statistics
   */
  calculateSummary(data) {
    const totalEntries = data.length;
    const totalHours = data.reduce((sum, entry) => {
      return sum + (entry.total_hours || 0);
    }, 0);
    
    const averageHours = totalEntries > 0 ? totalHours / totalEntries : 0;
    
    // Count entries by type
    const entryTypes = data.reduce((acc, entry) => {
      const type = entry.entry_type || 'regular';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    // Get date range
    const dates = data.map(entry => new Date(entry.clock_in_time)).filter(date => !isNaN(date));
    const earliestDate = dates.length > 0 ? new Date(Math.min(...dates)) : null;
    const latestDate = dates.length > 0 ? new Date(Math.max(...dates)) : null;

    return {
      totalEntries,
      totalHours: Math.round(totalHours * 100) / 100,
      averageHours: Math.round(averageHours * 100) / 100,
      entryTypes,
      dateRange: {
        earliest: earliestDate,
        latest: latestDate
      }
    };
  }

  /**
   * Get punch history for multiple employees (for bulk operations)
   */
  async getBulkPunchHistory(employeeIds, options = {}) {
    try {
      const {
        limit = 10,
        startDate = null,
        endDate = null
      } = options;

      let query = supabase
        .from('time_entries')
        .select(`
          id,
          employee_id,
          clock_in_time,
          clock_out_time,
          entry_type,
          total_hours,
          notes
        `)
        .in('employee_id', employeeIds)
        .order('clock_in_time', { ascending: false });

      // Apply date filters if provided
      if (startDate) {
        query = query.gte('clock_in_time', startDate);
      }
      if (endDate) {
        query = query.lte('clock_in_time', endDate);
      }

      // Apply limit
      query = query.limit(limit);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching bulk punch history:', error);
        throw error;
      }

      // Group by employee_id
      const groupedData = (data || []).reduce((acc, entry) => {
        const employeeId = entry.employee_id;
        if (!acc[employeeId]) {
          acc[employeeId] = [];
        }
        acc[employeeId].push(entry);
        return acc;
      }, {});

      // Transform each employee's data
      const result = {};
      Object.keys(groupedData).forEach(employeeId => {
        result[employeeId] = this.transformPunchHistoryData(groupedData[employeeId]);
      });

      return result;
    } catch (error) {
      console.error('Error in getBulkPunchHistory:', error);
      throw error;
    }
  }
}

export default new PunchHistoryService();
